import isNil from 'lodash/isNil';
import { NavigateFunction } from 'react-router-dom';
import { and, assign, enqueueActions, not, setup } from 'xstate';
import { SearchResultItem } from '@/components/global-search/type';
import { getAvailableTypes, getSelectedResults } from '@/components/global-search/utils';
import { TrackEventType } from '@/hooks/usePosthogTracking';

export type GlobalSearchEvents =
  | { type: 'CLOSE' }
  | { type: 'OPEN' }
  | { type: 'UP' }
  | { type: 'DOWN' }
  | { type: 'LEFT' }
  | { type: 'RIGHT' }
  | { type: 'TAB' }
  | { type: 'SELECT_TAB'; selectedTabIndex?: number }
  | { type: 'RESET' }
  | { type: 'CHANGE_INPUT'; query: string }
  | {
      type: 'SYSTEM_SET_NAVIGATION_RESULTS';
      results: SearchResultItem[];
    }
  | {
      type: 'SYSTEM_FETCH_ITEMS_SUCCESS';
      results: SearchResultItem[];
    }
  | { type: 'SYSTEM_FETCH_ITEMS_ERROR' }
  | { type: 'SELECT_ITEM'; trackEvent: TrackEventType; navigate: NavigateFunction };

type GlobalSearchContext = {
  query: string;
  results: SearchResultItem[];
  tabs: SearchResultItem['type'][];
  selectedTabResults: SearchResultItem[];
  selectedTabIndex: number;
  selectedResultItemIndex: number | null;
  selectedResultItem: SearchResultItem | null;
  selectedResultItemNavigationActionIndex: number;
};

const initialContext: GlobalSearchContext = {
  query: '',
  results: [],
  tabs: [],
  selectedTabResults: [],
  selectedTabIndex: 0,
  selectedResultItemIndex: null,
  selectedResultItem: null,
  selectedResultItemNavigationActionIndex: 0,
};

export const globalSearchMachine = setup({
  types: {
    context: {} as GlobalSearchContext,
    events: {} as GlobalSearchEvents,
  },
  guards: {
    isEmptyQuery: ({ event }) => event.type === 'CHANGE_INPUT' && event.query === '',
    areEmptyResults: ({ context }) => context.results.length === 0,
    tabsExist: ({ context }) => context.tabs.length > 0,
    onlyNavigationResultsExist: ({ context }) => context.results.every((result) => result.type === 'navigation'),
    selectedItemHasActions: ({ context }) => {
      const selectedItem = context.selectedResultItem;
      if (isNil(selectedItem) || isNil(selectedItem.navigationOptions)) return false;
      return selectedItem.navigationOptions.length > 0;
    },
    isAtFirstItem: ({ context }) => context.selectedResultItemIndex === 0,
    isAtLastItem: ({ context }) => context.selectedResultItemIndex === context.selectedTabResults.length - 1,
    isItemSelected: ({ context }) => !isNil(context.selectedResultItem),
  },
  actions: {
    changeInput: assign({
      query: ({ context, event }) => (event.type === 'CHANGE_INPUT' ? event.query : context.query),
    }),
    reset: assign(initialContext),
    setNavigationResults: assign({
      results: ({ event }) => (event.type === 'SYSTEM_SET_NAVIGATION_RESULTS' ? event.results : []),
      selectedTabResults: ({ event }) => (event.type === 'SYSTEM_SET_NAVIGATION_RESULTS' ? event.results : []),
    }),
    fetchItemsSuccess: enqueueActions(({ event, enqueue }) => {
      if (event.type !== 'SYSTEM_FETCH_ITEMS_SUCCESS') return;

      enqueue.assign({
        results: event.results,
        tabs: getAvailableTypes(event.results),
        selectedTabIndex: 0,
        selectedTabResults: getSelectedResults({ results: event.results, selectedTabType: 'all' }),
        selectedResultItemIndex: null,
        selectedResultItem: null,
        selectedResultItemNavigationActionIndex: 0,
      });
    }),
    selectFirstItem: assign({
      selectedResultItemIndex: 0,
      selectedResultItem: ({ context }) => context.selectedTabResults[0],
    }),
    selectLastItem: assign({
      selectedResultItemIndex: ({ context }) => context.selectedTabResults.length - 1,
      selectedResultItem: ({ context }) => context.selectedTabResults[context.selectedTabResults.length - 1],
    }),
    clearItemSelection: assign({
      selectedResultItemIndex: null,
      selectedResultItem: null,
      selectedResultItemNavigationActionIndex: 0,
    }),
    navigateTabLeft: enqueueActions(({ context, enqueue }) => {
      const currentSelectedTabIndex = context.selectedTabIndex;
      let nextSelectedTabIndex: number;
      if (currentSelectedTabIndex - 1 < 0) {
        nextSelectedTabIndex = context.tabs.length - 1;
      } else {
        nextSelectedTabIndex = currentSelectedTabIndex - 1;
      }

      enqueue.assign({
        selectedTabIndex: nextSelectedTabIndex,
        selectedTabResults: getSelectedResults({
          results: context.results,
          selectedTabType: context.tabs[nextSelectedTabIndex],
        }),
        selectedResultItemIndex: null,
        selectedResultItem: null,
        selectedResultItemNavigationActionIndex: 0,
      });
    }),
    navigateTabRight: enqueueActions(({ context, enqueue }) => {
      const currentSelectedTabIndex = context.selectedTabIndex;
      let nextSelectedTabIndex: number;
      if (currentSelectedTabIndex + 1 > context.tabs.length - 1) {
        nextSelectedTabIndex = 0;
      } else {
        nextSelectedTabIndex = currentSelectedTabIndex + 1;
      }

      enqueue.assign({
        selectedTabIndex: nextSelectedTabIndex,
        selectedTabResults: getSelectedResults({
          results: context.results,
          selectedTabType: context.tabs[nextSelectedTabIndex],
        }),
        selectedResultItemIndex: null,
        selectedResultItem: null,
        selectedResultItemNavigationActionIndex: 0,
      });
    }),
    navigateItemDown: enqueueActions(({ context, enqueue }) => {
      const currentSelectedResultItemIndex = context.selectedResultItemIndex;
      let nextSelectedResultItemIndex: number | null = null;
      if (!isNil(currentSelectedResultItemIndex)) {
        nextSelectedResultItemIndex = currentSelectedResultItemIndex + 1;
      }

      enqueue.assign({
        selectedResultItemIndex: nextSelectedResultItemIndex,
        selectedResultItem: ({ context }) =>
          !isNil(nextSelectedResultItemIndex) ? context.selectedTabResults[nextSelectedResultItemIndex] : null,
        selectedResultItemNavigationActionIndex: 0,
      });
    }),
    navigateItemUp: enqueueActions(({ context, enqueue }) => {
      const currentSelectedResultItemIndex = context.selectedResultItemIndex;
      let nextSelectedResultItemIndex: number | null = null;
      if (!isNil(currentSelectedResultItemIndex)) {
        nextSelectedResultItemIndex = currentSelectedResultItemIndex - 1;
      }

      enqueue.assign({
        selectedResultItemIndex: nextSelectedResultItemIndex,
        selectedResultItem: ({ context }) =>
          !isNil(nextSelectedResultItemIndex) ? context.selectedTabResults[nextSelectedResultItemIndex] : null,
        selectedResultItemNavigationActionIndex: 0,
      });
    }),
    navigateItemActionLeft: assign({
      selectedResultItemNavigationActionIndex: ({ context }) => {
        const navigationItems = context.selectedResultItem?.navigationOptions ?? [];
        if (context.selectedResultItemNavigationActionIndex - 1 < 0) {
          return navigationItems.length - 1;
        }
        return context.selectedResultItemNavigationActionIndex - 1;
      },
    }),
    navigateItemActionRight: assign({
      selectedResultItemNavigationActionIndex: ({ context }) => {
        const navigationItems = context.selectedResultItem?.navigationOptions ?? [];
        if (context.selectedResultItemNavigationActionIndex + 1 > navigationItems.length - 1) {
          return 0;
        }
        return context.selectedResultItemNavigationActionIndex + 1;
      },
    }),
    selectTab: enqueueActions(({ context, event, enqueue }) => {
      let nextSelectedTabIndex: number;
      if (event.type === 'SELECT_TAB' && !isNil(event.selectedTabIndex)) {
        nextSelectedTabIndex = event.selectedTabIndex;
      } else {
        nextSelectedTabIndex = context.selectedTabIndex;
      }

      enqueue.assign({
        selectedTabIndex: nextSelectedTabIndex,
        selectedTabResults: getSelectedResults({
          results: context.results,
          selectedTabType: context.tabs[nextSelectedTabIndex],
        }),
      });
    }),
    selectItem: ({ context, event }) => {
      const selectedItem = context.selectedResultItem;
      if (event.type === 'SELECT_ITEM' && !isNil(selectedItem)) {
        const { trackEvent, navigate } = event;

        let path = selectedItem.path;

        const selectedResultItemNavigationActionIndex = context.selectedResultItemNavigationActionIndex;
        if (
          selectedItem.navigationOptions &&
          selectedItem.navigationOptions.length > 0 &&
          !isNil(selectedResultItemNavigationActionIndex)
        ) {
          path = selectedItem.navigationOptions[selectedResultItemNavigationActionIndex].path || selectedItem.path;
        }

        trackEvent('GLOBAL_SEARCH_RESULT_CLICKED', {
          title: selectedItem.title,
          path: path,
          search_query: context.query,
          type: selectedItem.type,
        });

        if (path.includes('http')) {
          window.open(path);
        } else {
          navigate(path, { replace: false });
        }
      }
    },
  },
}).createMachine({
  context: initialContext,
  id: 'global-search',
  initial: 'closed',
  states: {
    closed: {
      on: {
        OPEN: {
          target: '#global-search.open',
        },
      },
      entry: 'reset',
    },
    open: {
      initial: 'idle',
      on: {
        CLOSE: {
          target: 'closed',
          actions: 'reset',
        },
        RESET: {
          target: 'open',
          actions: 'reset',
        },
        CHANGE_INPUT: [
          {
            target: '#global-search.open',
            guard: 'isEmptyQuery',
            actions: 'reset',
          },
          {
            target: '#global-search.open.loading',
            guard: not('isEmptyQuery'),
            actions: 'changeInput',
          },
        ],
      },
      states: {
        idle: {
          on: {
            SYSTEM_SET_NAVIGATION_RESULTS: {
              target: 'navigating',
              actions: 'setNavigationResults',
            },
          },
        },
        loading: {
          on: {
            SYSTEM_FETCH_ITEMS_SUCCESS: {
              target: 'success',
              actions: 'fetchItemsSuccess',
            },
            SYSTEM_FETCH_ITEMS_ERROR: {
              target: 'error',
            },
          },
        },
        success: {
          always: [
            { target: 'no-results', guard: 'areEmptyResults' },
            { target: '#global-search.open.navigating.tabs', guard: 'tabsExist' },
            { target: '#global-search.open.navigating.items', guard: 'onlyNavigationResultsExist' },
          ],
        },
        error: {
          always: {
            target: 'no-results',
          },
        },
        'no-results': {},
        navigating: {
          initial: 'tabs',
          on: {
            CLOSE: { target: '#global-search.closed' },
            SELECT_ITEM: { target: 'select', guard: 'isItemSelected', actions: 'selectItem' },
            SELECT_TAB: { target: '#global-search.open.navigating.tabs', actions: 'selectTab' },
          },
          states: {
            tabs: {
              on: {
                LEFT: { actions: 'navigateTabLeft', guard: 'tabsExist' },
                RIGHT: { actions: 'navigateTabRight', guard: 'tabsExist' },
                DOWN: {
                  target: '#global-search.open.navigating.items',
                  actions: 'selectFirstItem',
                },
                UP: { target: '#global-search.open.navigating.items', actions: 'selectLastItem' },
                TAB: { actions: 'navigateTabRight', guard: 'tabsExist' },
              },
            },
            items: {
              always: [
                {
                  target: '#global-search.open.navigating.itemActions',
                  guard: 'selectedItemHasActions',
                },
              ],
              on: {
                UP: [
                  {
                    target: '#global-search.open.navigating.items',
                    guard: and(['isAtFirstItem', 'onlyNavigationResultsExist']),
                    actions: 'selectLastItem',
                  },
                  {
                    target: '#global-search.open.navigating.tabs',
                    guard: 'isAtFirstItem',
                    actions: 'clearItemSelection',
                  },
                  { actions: 'navigateItemUp' },
                ],
                DOWN: [
                  {
                    target: '#global-search.open.navigating.items',
                    guard: and(['isAtLastItem', 'onlyNavigationResultsExist']),
                    actions: 'selectFirstItem',
                  },
                  {
                    target: '#global-search.open.navigating.tabs',
                    guard: 'isAtLastItem',
                    actions: 'clearItemSelection',
                  },
                  { actions: 'navigateItemDown' },
                ],
                LEFT: { target: '#global-search.open.navigating.tabs', guard: 'tabsExist', actions: 'navigateTabLeft' },
                RIGHT: {
                  target: '#global-search.open.navigating.tabs',
                  guard: 'tabsExist',
                  actions: 'navigateTabRight',
                },
                TAB: { target: '#global-search.open.navigating.tabs', guard: 'tabsExist', actions: 'navigateTabRight' },
              },
            },
            itemActions: {
              on: {
                LEFT: { target: '#global-search.open.navigating.itemActions', actions: 'navigateItemActionLeft' },
                RIGHT: { target: '#global-search.open.navigating.itemActions', actions: 'navigateItemActionRight' },
                UP: [
                  {
                    target: '#global-search.open.navigating.tabs',
                    guard: 'isAtFirstItem',
                    actions: 'clearItemSelection',
                  },
                  { target: '#global-search.open.navigating.items', actions: 'navigateItemUp' },
                ],
                DOWN: [
                  {
                    target: '#global-search.open.navigating.tabs',
                    guard: 'isAtLastItem',
                    actions: 'clearItemSelection',
                  },
                  { target: '#global-search.open.navigating.items', actions: 'navigateItemDown' },
                ],
                TAB: { target: '#global-search.open.navigating.tabs', actions: 'navigateTabRight' },
              },
            },
          },
        },
        select: {
          always: {
            target: '#global-search.closed',
          },
        },
      },
    },
  },
});
